import { useEffect, useRef } from 'react';
import { Box, Flex, Pagination, Skeleton, Text } from '@mantine/core';
import { IconBulb } from '@tabler/icons';
import {
  EXPLORE_IDEAS_LIST_PAGE_SIZE,
  useExploreIdeasListPage,
} from '../hooks/useExploreIdeasListPage';
import ExploreIdeaListRow from './ExploreIdeaListRow';

export interface ExploreIdeasListCardProps {
  ideaIds: string[];
  artifactId?: string;
  page: number;
  onPageChange: (page: number) => void;
  onRowClick: (ideaId: string) => void;
  selectedIdeaId?: string;
  isSelectionActive?: boolean;
  hasUrlIdeasPage?: boolean;
}

export function ExploreIdeasListCard({
  ideaIds,
  artifactId,
  page,
  onPageChange,
  onRowClick,
  selectedIdeaId = '',
  isSelectionActive = true,
  hasUrlIdeasPage = false,
}: ExploreIdeasListCardProps) {
  const { rows, totalPages, pageStartIndex, pageEndIndex, isPageLoading } =
    useExploreIdeasListPage(ideaIds, page);
  const showPagination = ideaIds.length > EXPLORE_IDEAS_LIST_PAGE_SIZE;

  const previousSelectedIdeaIdRef = useRef(selectedIdeaId);
  const isInitialSelectionSyncRef = useRef(true);

  useEffect(() => {
    if (!selectedIdeaId || !isSelectionActive) {
      previousSelectedIdeaIdRef.current = selectedIdeaId;
      return;
    }

    if (isInitialSelectionSyncRef.current && hasUrlIdeasPage) {
      isInitialSelectionSyncRef.current = false;
      previousSelectedIdeaIdRef.current = selectedIdeaId;
      return;
    }

    const selectedIndex = ideaIds.indexOf(selectedIdeaId);
    if (selectedIndex === -1) {
      previousSelectedIdeaIdRef.current = selectedIdeaId;
      isInitialSelectionSyncRef.current = false;
      return;
    }

    const targetPage = Math.floor(selectedIndex / EXPLORE_IDEAS_LIST_PAGE_SIZE) + 1;
    const selectedIdeaChanged = previousSelectedIdeaIdRef.current !== selectedIdeaId;
    previousSelectedIdeaIdRef.current = selectedIdeaId;
    isInitialSelectionSyncRef.current = false;

    if (!selectedIdeaChanged) {
      return;
    }

    if (targetPage !== page) {
      onPageChange(targetPage);
    }
  }, [selectedIdeaId, ideaIds, isSelectionActive, onPageChange, page, hasUrlIdeasPage]);

  return (
    <Box
      mt="xs"
      mb="md"
      data-testid="explore-ideas-list-card"
      {...(artifactId ? { 'data-artifact-id': artifactId } : {})}
      sx={{
        borderRadius: 8,
        border: '1px solid #D8D8DB',
        backgroundColor: '#FAFAFA',
        boxShadow: '0px 1px 5px 0px #00124712',
        overflow: 'hidden',
      }}
    >
      <Flex
        align="center"
        px="md"
        py="sm"
        sx={{ borderBottom: '1px solid #ECECEF' }}
      >
        <Flex align="center" gap={8}>
          <IconBulb size={18} color="#5C5CEB" />
          <Text fw={600} size="sm" c="#5C5CEB" data-testid="explore-ideas-list-count">
            {ideaIds.length}
          </Text>
          <Text fw={600} size="sm" c="#212529" sx={{ letterSpacing: 0.4 }}>
            {ideaIds.length == 1 ? 'IDEA' : 'IDEAS'}
          </Text>
        </Flex>
      </Flex>

      <Box>
        {isPageLoading
          ? Array.from({ length: Math.min(EXPLORE_IDEAS_LIST_PAGE_SIZE, ideaIds.length) }).map(
            (_, index) => (
              <Box
                key={index}
                py="sm"
                px="md"
                sx={{ borderBottom: '1px solid #ECECEF', '&:last-of-type': { borderBottom: 'none' } }}
              >
                <Flex justify="space-between" align="center" gap="md" sx={{ minHeight: 56 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton height={18} width="55%" mb={8} />
                    <Flex align="center" gap={8}>
                      <Skeleton height={24} width={88} radius={6} />
                      <Skeleton height={24} width={96} radius={6} />
                      <Skeleton height={24} width={120} radius={6} />
                    </Flex>
                  </Box>
                  <Flex align="center" gap="lg" sx={{ flexShrink: 0 }}>
                    <Skeleton height={14} width={220} />
                  </Flex>
                </Flex>
              </Box>
            )
          )
          : rows.map((row) => (
            <ExploreIdeaListRow
              key={row.id}
              row={row}
              onRowClick={onRowClick}
              isSelected={
                isSelectionActive && Boolean(selectedIdeaId) && row.id === selectedIdeaId
              }
            />
          ))}
      </Box>

      {showPagination ? (
        <Flex
          align="center"
          justify="space-between"
          px="md"
          py="sm"
          sx={{ borderTop: '1px solid #ECECEF', backgroundColor: '#fff' }}
        >
          <Text size="sm" c="dimmed">
            {pageStartIndex + 1}-{pageEndIndex} of {ideaIds.length} ideas
          </Text>
          <Pagination total={totalPages} value={page} onChange={onPageChange} size="sm" withEdges />
        </Flex>
      ) : null}
    </Box>
  );
}

export default ExploreIdeasListCard;
